import { isAxiosError } from 'axios'

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504])
const CONFIRMED_SESSION_EXPIRY_MARKERS = new Set(['session_expired', 'token_expired'])

export function isRetryableAuthError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return false
  }

  if (error.code === 'ECONNABORTED') {
    return true
  }

  if (error.code === 'ERR_CANCELED') {
    return false
  }

  if (!error.response) {
    return true
  }

  return RETRYABLE_STATUS_CODES.has(error.response.status)
}

export function isConfirmedSessionExpiryPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') {
    return false
  }

  const body = payload as Record<string, unknown>
  const category = typeof body.category === 'string' ? body.category.toLowerCase() : ''
  const errorCode = typeof body.error === 'string' ? body.error.toLowerCase() : ''

  return CONFIRMED_SESSION_EXPIRY_MARKERS.has(category)
    || CONFIRMED_SESSION_EXPIRY_MARKERS.has(errorCode)
}

export function isConfirmedSessionExpiryError(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 401) {
    return false
  }

  return isConfirmedSessionExpiryPayload(error.response?.data)
}

export async function runWithAuthRetry<T>(
  operation: () => Promise<T>,
  maxAttempts = 2,
  baseDelayMs = 400,
  onRetry?: (nextAttempt: number, backoffMs: number) => void
): Promise<T> {
  let lastError: unknown

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await operation()
    } catch (error) {
      lastError = error

      const canRetry = attempt < maxAttempts && isRetryableAuthError(error)
      if (!canRetry) {
        throw error
      }

      const backoffMs = baseDelayMs * (2 ** (attempt - 1))
      if (onRetry) {
        onRetry(attempt + 1, backoffMs)
      }
      await new Promise<void>((resolve) => {
        globalThis.setTimeout(resolve, backoffMs)
      })
    }
  }

  throw (lastError instanceof Error ? lastError : new Error('Authentication request failed'))
}
